/**
 * React hooks for GTD Inbox operations
 * 
 * These hooks use TanStack Query for caching and state management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GTDClient } from '../../../client';
import { 
  CaptureInboxItemRequest,
  ClarifyInboxItemRequest,
  ProcessInboxItemRequest,
  InboxQueryFilters,
  ListQueryParams 
} from '../../../contracts';
import { useGTDClient } from '../provider';

// Query keys
const INBOX_QUERY_KEY = ['gtd', 'inbox'] as const;

export function useInboxItems(filters?: InboxQueryFilters & ListQueryParams) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...INBOX_QUERY_KEY, filters],
    queryFn: () => client.getInboxItems(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useInboxItem(id: string | null) {
  const client = useGTDClient();
  
  return useQuery({
    queryKey: [...INBOX_QUERY_KEY, id],
    queryFn: () => client.getInboxItem(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useCaptureInboxItem() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CaptureInboxItemRequest) => 
      client.captureInboxItem(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
    },
  });
}

export function useClarifyInboxItem() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ClarifyInboxItemRequest }) =>
      client.clarifyInboxItem(id, request),
    onSuccess: (data, variables) => {
      // Update specific item
      queryClient.setQueryData([...INBOX_QUERY_KEY, variables.id], data);
      // Invalidate list
      queryClient.invalidateQueries({ 
        queryKey: INBOX_QUERY_KEY,
        exact: false 
      });
    },
  });
}

export function useProcessInboxItem() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: ProcessInboxItemRequest }) =>
      client.processInboxItem(id, request),
    onSuccess: () => {
      // Invalidate inbox and potentially projects/actions
      queryClient.invalidateQueries({ queryKey: ['gtd'] });
    },
  });
}

export function useDeleteInboxItem() {
  const client = useGTDClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.deleteInboxItem(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: [...INBOX_QUERY_KEY, id] });
      // Invalidate list
      queryClient.invalidateQueries({ 
        queryKey: INBOX_QUERY_KEY,
        exact: false 
      });
    },
  });
}